/**
 * The owner's lab-unit preference — SI or conventional — and the one place every
 * entry form and on-screen surface asks for it.
 *
 * In the spirit of weight/useWeightUnitPref.ts, and stored the same way: on the
 * user record (User.lab_unit), the server's copy winning, as DECISIONS.md DS-5 set
 * for preferences that belong to the person rather than to a browser.
 *
 * Lab entry used to open in mg/dL for everyone, so an owner in Ireland typing an SI
 * report had to flip every field, and the screens showed each analyte in whatever
 * unit the last panel happened to use (9 Oct 2026, finding 13). Now:
 *
 *  - ENTRY: every lab field opens in the preferred unit. The per-field selector
 *    still exists for a report that mixes units, and still only LABELS the number.
 *  - ON-SCREEN DISPLAY: tables, charts and finding sentences show the preferred
 *    unit, converting panels recorded in the other one.
 *  - DOCUMENTS FOR A VET (the vet sheet, the CSV) do not follow it: they report each
 *    analyte in the unit of the cat's most recent panel (labDisplayUnit.ts), which
 *    is the unit on the report the vet will have in front of them.
 *
 * Signup sets it, with the weight unit, from the browser locale: en-US gives
 * conventional and lb; every other locale SI and kg.
 */

import i18n from '../../i18n';
import { useAuthStore } from '../../stores/authStore';
import type { WeightDisplayUnit } from '../weight/types';

export type LabUnitPref = 'si' | 'conventional';

export const DEFAULT_LAB_UNIT: LabUnitPref = 'si';

export function isLabUnitPref(value: unknown): value is LabUnitPref {
  return value === 'si' || value === 'conventional';
}

/** The current user's stored preference, or the default when unauthenticated. */
export function getCurrentLabUnit(): LabUnitPref {
  const pref = useAuthStore.getState().user?.lab_unit;
  return isLabUnitPref(pref) ? pref : DEFAULT_LAB_UNIT;
}

/** React hook returning the current preference, re-rendering when it changes. */
export function useLabUnitPref(): LabUnitPref {
  return useAuthStore((state) => {
    const pref = state.user?.lab_unit;
    return isLabUnitPref(pref) ? pref : DEFAULT_LAB_UNIT;
  });
}

/**
 * The defaults a new account starts with, from the browser locale. US English
 * means conventional units and pounds: the US is the one place feline labs report
 * creatinine in mg/dL and clinics weigh in lb. Brazil reports conventional units
 * too (mg/dL) but weighs in kg, so Brazilian Portuguese — a pt* browser locale, or
 * the language chosen at signup — means conventional and kg, with the BUN field
 * as urea in mg/dL (labDisplayUnit.ts conventionalBunUnit). Spanish — an es*
 * browser locale, or Spanish chosen at signup — is the same: conventional and kg,
 * with the BUN field's unit set by the country (11 Oct 2026). Every other locale
 * this product is used in (Ireland, the UK, the EU, Australia) reports SI and
 * weighs in kg. DS-6.
 */
export function unitDefaultsForLocale(
  locale: string | undefined | null,
  language?: string | null,
): {
  lab_unit: LabUnitPref;
  weight_unit: WeightDisplayUnit;
} {
  const normalised = (locale ?? '').replace('_', '-').toLowerCase();
  if (normalised === 'en-us') return { lab_unit: 'conventional', weight_unit: 'lb' };
  if (/^pt(-|$)/.test(normalised) || language === 'pt-BR') return { lab_unit: 'conventional', weight_unit: 'kg' };
  if (/^es(-|$)/.test(normalised) || language === 'es') return { lab_unit: 'conventional', weight_unit: 'kg' };
  return { lab_unit: 'si', weight_unit: 'kg' };
}

/** Human labels for the two systems, as the form-level selector names them. */
export const LAB_UNIT_LABELS: Record<LabUnitPref, string> = {
  get si() { return i18n.t('labs.unitSystem.si'); },
  get conventional() { return i18n.t('labs.unitSystem.conventional'); },
};
